/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Word-Level Diff Computation Utility (Task 10.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

export type DiffChangeType = 'equal' | 'added' | 'removed';

export interface DiffToken {
  type: DiffChangeType;
  value: string;
}

/**
 * Tokenizes text into words and whitespace/punctuation tokens.
 */
export function tokenizeWords(text: string): string[] {
  if (!text) return [];
  // Split into words, punctuation, and whitespace with full Unicode support
  return text.match(/[\p{L}\p{N}]+|[^\s\p{L}\p{N}]+|\s+/gu) || [];
}

/**
 * Computes word-level difference using Longest Common Subsequence (LCS).
 * Compares sourceA (base/TM source) against sourceB (current source segment).
 */
export function computeWordDiff(sourceA: string, sourceB: string): DiffToken[] {
  if (!sourceA && !sourceB) return [];
  if (!sourceA) return [{ type: 'added', value: sourceB }];
  if (!sourceB) return [{ type: 'removed', value: sourceA }];
  if (sourceA === sourceB) return [{ type: 'equal', value: sourceA }];

  const tokensA = tokenizeWords(sourceA);
  const tokensB = tokenizeWords(sourceB);

  const n = tokensA.length;
  const m = tokensB.length;

  // Build DP table for LCS
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (tokensA[i - 1] === tokensB[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to assemble diff tokens
  const rawTokens: DiffToken[] = [];
  let i = n;
  let j = m;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && tokensA[i - 1] === tokensB[j - 1]) {
      rawTokens.push({ type: 'equal', value: tokensA[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      rawTokens.push({ type: 'added', value: tokensB[j - 1] });
      j--;
    } else if (i > 0 && (j === 0 || dp[i][j - 1] < dp[i - 1][j])) {
      rawTokens.push({ type: 'removed', value: tokensA[i - 1] });
      i--;
    }
  }

  rawTokens.reverse();

  // Merge consecutive tokens of the same type for cleaner rendering
  const merged: DiffToken[] = [];
  for (const token of rawTokens) {
    if (merged.length > 0 && merged[merged.length - 1].type === token.type) {
      merged[merged.length - 1].value += token.value;
    } else {
      merged.push({ ...token });
    }
  }

  return merged;
}

/**
 * Calculates a Levenshtein-based similarity percentage between two phrases (0-100).
 */
export function calculateSimilarityScore(str1: string, str2: string): number {
  if (str1 === str2) return 100;
  if (!str1 || !str2) return 0;

  const s1 = str1.trim().toLowerCase();
  const s2 = str2.trim().toLowerCase();
  if (s1 === s2) return 100;

  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;

  if (longer.length === 0) return 100;

  // Edit distance calculation
  const costs: number[] = [];
  for (let i = 0; i <= longer.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= shorter.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (longer.charAt(i - 1) !== shorter.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[shorter.length] = lastValue;
  }

  const editDistance = costs[shorter.length];
  const similarity = ((longer.length - editDistance) / longer.length) * 100;
  return Math.max(0, Math.min(100, Math.round(similarity)));
}
